/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRefresh,
  faShieldVirus,
  faShieldDog,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import RiskReviewQueue from 'components/tables/RiskReviewQueue';
import RiskFilterBar from 'components/common/RiskFilterBar';
import RiskKPICard3 from 'components/cards/RiskKPICard3';
import RiskKPICard2 from 'components/cards/RiskKPICard2';
import RiskTimeline from 'components/timelines/RiskTimeline';
import YearlyRiskChart from 'components/charts/YearlyRiskChart';
import AuthService from 'services/auth.service';
import { useMainLayoutContext } from 'providers/MainLayoutProvider';
import {
  fetchPendingRiskReviews,
  fetchRiskStatistics,
  fetchRiskEvolution
} from 'services/hvarisk.service';
import HVASpinner from 'components/base/HVASpinner';

interface RiskFilters {
  serviceId: number | null;
  campaignId: number | null;
  categoryId: number | null;
  riskElementId: number | null;
  severity: number | null;
  dateRange: string;
}

const RiskReviewDashboard: React.FC = () => {
  const { setContentClass } = useMainLayoutContext();
  const currentUser = AuthService.getCurrentUser();
  const currentUserId = currentUser?.id;

  const [filters, setFilters] = useState<RiskFilters>({
    serviceId: null,
    campaignId: null,
    categoryId: null,
    riskElementId: null,
    severity: null,
    dateRange: 'today'
  });

  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [riskStats, setRiskStats] = useState({
    activeRisks: 0,
    lastWeek: 0,
    lastMonth: 0,
    older: 0,
    criticalHigh30Days: 0,
    totalAnalyzed30Days: 0
  });
  const [riskEvolution30Days, setRiskEvolution30Days] = useState<any[]>([]);
  const [analyzedEvolution30Days, setAnalyzedEvolution30Days] = useState<any[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setContentClass('px-0 pt-navbar');
    return () => {
      setContentClass('');
    };
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);

        // Fechas para los últimos 30 días
        const dateto = new Date().toISOString().split('T')[0];
        const datefrom = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0];

        // Obtener estadísticas generales
        const stats = await fetchRiskStatistics({
          datefrom,
          dateto
        });

        // Obtener evolución de riesgos
        const evolution = await fetchRiskEvolution({
          datefrom,
          dateto,
          interval: 'daily'
        });

        // Procesar datos para las tarjetas
        if (stats && stats.length > 0) {
          const stat = stats[0];

          // Obtener riesgos pendientes por antigüedad
          const pendingRisks = await fetchPendingRiskReviews({
            limit: 100,
            offset: 0
          });

          // Calcular distribución por antigüedad
          const now = new Date();
          let lastWeek = 0;
          let lastMonth = 0;
          let older = 0;

          pendingRisks.risks.forEach(risk => {
            const riskDate = new Date(risk.iariskvaluedate);
            const daysDiff = Math.floor(
              (now.getTime() - riskDate.getTime()) / (1000 * 60 * 60 * 24)
            );

            if (daysDiff <= 7) {
              lastWeek++;
            } else if (daysDiff <= 30) {
              lastMonth++;
            } else {
              older++;
            }
          });

          setRiskStats({
            activeRisks: stat.pendingReviews || 0,
            lastWeek: lastWeek,
            lastMonth: lastMonth,
            older: older,
            criticalHigh30Days: stat.highSeverityRisks || 0,
            totalAnalyzed30Days: stat.totalRisks || 0
          });
        }

        // Procesar datos de evolución para gráficos
        const criticalHighData = evolution.map(day => ({
          date: day.date,
          count: (day.critical || 0) + (day.high || 0)
        }));

        const totalData = evolution.map(day => ({
          date: day.date,
          count: day.total || 0
        }));

        setRiskEvolution30Days(criticalHighData);
        setAnalyzedEvolution30Days(totalData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Mantener datos vacíos en caso de error
        setRiskStats({
          activeRisks: 0,
          lastWeek: 0,
          lastMonth: 0,
          older: 0,
          criticalHigh30Days: 0,
          totalAnalyzed30Days: 0
        });
        setRiskEvolution30Days([]);
        setAnalyzedEvolution30Days([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  // const handleExport = () => {
  //   console.log('Exporting data with filters:', filters);
  // };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(Number(e.target.value));
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = 2020; year <= currentYear; year++) {
    yearOptions.push(year);
  }

  return (
    <>
      {isLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '100vh' }}
        >
          <HVASpinner />
        </div>
      ) : (
        <>
          <Row className="g-0">
            <Col xs={12} xxl={9} className="px-0">
              <div className="px-4 px-lg-6 pt-5 pb-3 bg-body">
                <div className="mb-5">
                  <div className="d-flex justify-content-between">
                    <h2 className="text-body-emphasis fw-bolder mb-2">
                      Risk Review Dashboard
                    </h2>
                  </div>
                </div>

                <Row className="g-3 mb-3">
                  <Col xs={12} sm={12} md={12} xl={4} xxl={4}>
                    <RiskKPICard3
                      icon={faTriangleExclamation}
                      color="primary"
                      label="Pending Review"
                      label2="Detected last week: "
                      label3="Detected last month: "
                      label4="Older detections: "
                      value={riskStats.activeRisks}
                      value2={riskStats.lastWeek}
                      value3={riskStats.lastMonth}
                      value4={riskStats.older}
                      title="Active Risks"
                    />
                  </Col>
                  <Col xs={12} sm={12} md={6} xl={4} xxl={4}>
                    <RiskKPICard2
                      icon={faShieldVirus}
                      color="primary"
                      label="Last 30 days"
                      title="Detected"
                      value={riskStats.criticalHigh30Days}
                      data={riskEvolution30Days}
                    />
                  </Col>
                  <Col xs={12} sm={12} md={6} xl={4} xxl={4}>
                    <RiskKPICard2
                      icon={faShieldDog}
                      color="primary"
                      label="Last 30 days"
                      title="Analyzed"
                      value={riskStats.totalAnalyzed30Days}
                      data={analyzedEvolution30Days}
                    />
                  </Col>
                </Row>

                <Row className="gx-1 gx-sm-5 gy-4 mb-3 px-2 pt-2 justify-content-between">
                  <Col md={9} xs="auto">
                    <div>
                      <h4 className="mb-1">Critical & High Risk Trends</h4>
                    </div>
                    <div className="text-body-secondary mb-0 mt-0 fs-10 lh-1">
                      Monitor the evolution of critical (severity 5) and high
                      (severity 4) risks
                      <br />
                      throughout the year to identify patterns and take
                      preventive actions.
                      <br />
                      Spikes may indicate systemic issues requiring immediate
                      attention.
                    </div>
                  </Col>
                  <Col
                    md={3}
                    xs="auto"
                    className="d-flex pt-3 pe-6 justify-content-end text-end"
                  >
                    <Form.Select
                      size="sm"
                      value={selectedYear}
                      onChange={handleYearChange}
                      className="w-auto"
                    >
                      {yearOptions.map(year => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>

                <Row className="gx-1 gx-sm-5 gy-4 mb-0 px-2">
                  <div>
                    <YearlyRiskChart selectedYear={selectedYear} />
                  </div>
                </Row>
              </div>
            </Col>

            <Col xs={12} xxl={3} className="px-0 border-start-xxl">
              <div className="h-100">
                <div className="bg-light dark__bg-gray-1100 h-100">
                  <div className="p-4 p-lg-5 pb-0 pb-lg-0">
                    <Row className="d-flex justify-content-between align-items-center">
                      <Col xs="auto" xxl="auto" className="pe-0">
                        <h4 className="text-body-highlight fw-bold mb-0">
                          Recent Critical Risks
                        </h4>
                      </Col>
                    </Row>
                  </div>
                  <div className="p-4 p-lg-5 pt-0 pt-lg-0 pb-lg-3">
                    <div className="fs-9 fw-normal text-body-terciary pt-0 pb-4">
                      (Severity 4 & 5)
                    </div>
                    <RiskTimeline />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="g-0">
            <Col xs={12} xxl={12} className="px-0">
              <div className="px-4 px-lg-7 pt-6 pb-6 bg-body-emphasis dark__bg-gray-1100 border-top">
                {/* Header con acciones */}
                <Row className="gy-3 mb-4 justify-content-between">
                  <Col md={6}>
                    <h3 className="mb-2 text-body-emphasis">
                      Risk Review Queue
                    </h3>
                    <h5 className="text-body-tertiary fw-semibold">
                      Manage and review detected risks
                    </h5>
                  </Col>
                  <Col md={6} className="text-end">
                    <Button
                      variant="link"
                      size="sm"
                      className="me-2"
                      onClick={handleRefresh}
                    >
                      <FontAwesomeIcon icon={faRefresh} className="me-1" />
                      Refresh Data
                    </Button>
                    {/* <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleExport}
                    >
                      <FontAwesomeIcon icon={faDownload} className="me-1" />
                      Export
                    </Button> */}
                  </Col>
                </Row>

                {/* Barra de filtros */}
                <Row className="mb-0">
                  <Col xs={12}>
                    <RiskFilterBar
                      filters={filters}
                      onFilterChange={setFilters}
                    />
                  </Col>
                </Row>

                {/* Cola de revisión principal */}
                <Row>
                  <Col xs={12}>
                    <div>
                      <div className="p-0 pt-2">
                        <RiskReviewQueue
                          filters={filters}
                          refreshKey={refreshKey}
                          currentUserId={currentUserId}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default RiskReviewDashboard;
